// CalendarPanel.jsx
import { useMemo, useState } from "react";
import { Calendar } from "@/components/ui/calendar";
import { ScrollArea } from "@/components/ui/scroll-area";
import { useCalendarData } from "@/hooks/useCalendarData";
import {
  ChevronLeft,
  ChevronRight,
  Calendar as CalendarIcon,
  Grid3X3,
  List,
  Clock,
  MapPin,
  ExternalLink,
  Plus,
  Edit2,
  Trash2,
  Link as LinkIcon,
  Info,
} from "lucide-react";
import CalendarEventModal from "@/components/CalendarEventModal";

const VIEW_MODES = {
  MONTH: "month",
  WEEK: "week",
  DAY: "day",
};

const API_BASE = process.env.REACT_APP_API_BASE || "/api";
const GOOGLE_OAUTH_URL = `${API_BASE}/auth/google`;
const OUTLOOK_OAUTH_URL = `${API_BASE}/auth/outlook`;

function ColoredIcon({ src, alt }) {
  return <img src={src} alt={alt} className="w-4 h-4" />;
}

function DayEventsList({ date, events, onEdit, onDelete }) {
  const dayStr = date?.toISOString?.().split("T")[0];
  const todays = (events || []).filter((e) => (e.start || e.start_time || "").slice(0,10) === dayStr);
  if (todays.length === 0) return null;
  return (
    <div className="border border-white/10 rounded-lg p-3">
      <div className="text-xs text-soft mb-2">Events on {dayStr}</div>
      <div className="space-y-2">
        {todays.map((e) => (
          <div key={e.id} className="flex items-center justify-between bg-black/30 border border-white/10 rounded p-2">
            <div>
              <div className="text-sm">{e.title || "(No title)"}</div>
              <div className="text-[11px] text-soft">
                {new Date(e.start || e.start_time).toLocaleTimeString([], {hour:'numeric', minute:'2-digit'})}
              </div>
            </div>
            <div className="flex gap-2">
              <button className="console-button text-xs px-2 py-1" onClick={() => onEdit(e)}>
                <Edit2 className="w-3 h-3" /> Edit
              </button>
              <button className="console-button text-xs px-2 py-1" onClick={() => onDelete(e.id)}>
                <Trash2 className="w-3 h-3" /> Delete
              </button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

export default function CalendarPanel({
  onDateSelect,
  isConnected = false,
  onConnectGoogle,
  onConnectOutlook,
}) {
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [viewMode, setViewMode] = useState(VIEW_MODES.MONTH);
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const [connectOpen, setConnectOpen] = useState(false);
  const { events, add, edit, remove } = useCalendarData();
  const [showEventModal, setShowEventModal] = useState(false);
  const [editingEvent, setEditingEvent] = useState(null);

  const safeEvents = Array.isArray(events) ? events : [];

  const datesWithEvents = useMemo(
    () =>
      safeEvents
        .map((e) => e.start?.split("T")[0] || e.date)
        .filter(Boolean)
        .map((d) => new Date(`${d}T00:00:00`)),
    [safeEvents]
  );

  const connectGoogle = () => {
    if (typeof onConnectGoogle === "function") return onConnectGoogle();
    window.location.href = GOOGLE_OAUTH_URL;
  };

  const connectOutlook = () => {
    if (typeof onConnectOutlook === "function") return onConnectOutlook();
    window.location.href = OUTLOOK_OAUTH_URL;
  };

  // =========================
  // NOT CONNECTED UI
  // =========================
  if (!isConnected) {
    return (
      <div className="w-full flex flex-col items-center text-center gap-3 py-3">
        <CalendarIcon className="w-8 h-8 text-white/70" />

        <div className="text-soft text-sm max-w-sm leading-relaxed">
          Connect your calendars for time-aware briefings and task coordination.
        </div>

        {/* Primary action */}
        <button
          type="button"
          className="console-button px-4 py-2 text-sm"
          onClick={() => setConnectOpen((v) => !v)}
        >
          <LinkIcon className="w-4 h-4" />
          Connect Calendar
        </button>

        {connectOpen && (
          <div className="w-full max-w-sm pt-1 flex flex-col items-center gap-2">
            <button
              type="button"
              className="console-button px-4 py-2 text-sm"
              onClick={connectGoogle}
            >
              <ColoredIcon
                src="/icons/google-calendar.svg"
                alt="Google Calendar"
              />
              <span>Google Calendar</span>
            </button>

            <button
              type="button"
              className="console-button px-4 py-2 text-sm"
              onClick={connectOutlook}
            >
              <ColoredIcon
                src="/icons/outlook-calendar.png"
                alt="Outlook Calendar"
              />
              <span>Outlook Calendar</span>
            </button>

            <button
              type="button"
              className="text-xs text-neon-cyan/80 hover:text-neon-cyan underline underline-offset-4 flex items-center gap-2 justify-center w-full pt-1"
              onClick={() => {
                alert(
                  "Calendar lets Vox see upcoming events, time your briefings, and schedule tasks around real availability."
                );
              }}
            >
              <Info className="w-3 h-3" />
              What does this allow?
            </button>
          </div>
        )}
      </div>
    );
  }

  // =========================
  // CONNECTED UI
  // =========================
  return (
    <div className="w-full h-full flex flex-col gap-4">
      {/* View mode buttons + Add Event */}
      <div className="flex items-center gap-2">
        <div className="flex-1 flex gap-1 p-1 rounded-lg bg-black/30 border border-white/10">
          {[
            [VIEW_MODES.MONTH, Grid3X3, "Month"],
            [VIEW_MODES.WEEK, List, "Week"],
            [VIEW_MODES.DAY, CalendarIcon, "Day"],
          ].map(([mode, Icon, label]) => (
            <button
              key={mode}
              onClick={() => setViewMode(mode)}
              className={`flex-1 flex items-center justify-center gap-1 px-2 py-1.5 text-xs rounded-md transition-all ${
                viewMode === mode
                  ? "bg-neon-cyan/20 text-neon-cyan shadow-[0_0_10px_rgba(0,246,255,0.35)]"
                  : "text-soft hover:text-white"
              }`}
            >
              <Icon className="w-3 h-3" />
              {label}
            </button>
          ))}
        </div>
        <button className="console-button text-xs px-3 py-1" onClick={() => setShowEventModal(true)}>
          <Plus className="w-3 h-3" /> Add Event
        </button>
      </div>

      {/* Month view */}
      {viewMode === VIEW_MODES.MONTH ? (
        <div className="flex flex-col gap-3">
          <div className="flex justify-center">
            <Calendar
              mode="single"
              selected={selectedDate}
              onSelect={setSelectedDate}
              month={currentMonth}
              onMonthChange={setCurrentMonth}
              modifiers={{ hasEvents: datesWithEvents }}
              modifiersStyles={{
                hasEvents: {
                  textDecoration: "underline",
                  textDecorationColor: "#00f6ff",
                },
              }}
              className="rounded-md"
            />
          </div>
          <DayEventsList
            date={selectedDate}
            events={safeEvents}
            onEdit={(e) => { setEditingEvent(e); setShowEventModal(true); }}
            onDelete={(id) => remove(id)}
          />
        </div>
      ) : null}

      {/* Event modal */}
      {showEventModal ? (
        <CalendarEventModal
          isOpen={true}
          onClose={() => { setShowEventModal(false); setEditingEvent(null); }}
          onSave={async (evt) => {
            if (editingEvent) await edit({ ...editingEvent, ...evt });
            else await add(evt);
            setShowEventModal(false);
            setEditingEvent(null);
          }}
          onDelete={async (id) => {
            await remove(id);
            setShowEventModal(false);
            setEditingEvent(null);
          }}
          event={editingEvent}
          selectedDate={selectedDate}
        />
      ) : null}
    </div>
  );
}

